import { AJAX_TIMEOUT_MESSAGE } from '../helpers.js';
import * as FieldValidation from '../shared/field-validation.js';
import { initPledgesPageContent } from '../manager/pledges/runtime-registry';
import { getKioskConfig } from '../shared/kiosk-config';
import { cleanupBootstrapModalBody } from '../shared/bootstrap-modal-cleanup.js';
import { checkoutIntentFor, clearCheckoutIntent } from '../shared/checkout-intent.js';
import { chargeWentThrough } from '../shared/charge-went-through.js';
import { htmlShowsPaymentReceivedPage } from '../shared/payment-received-page.js';
import { applyChargeSuccess } from '../shared/apply-charge-success.js';
import { getMemberOverlayElement, showOverlay, hideOverlay } from '../shared/overlay.js';
import { formatCardNumberInput } from '../shared/payment-method-form.js';
import { openIndexPage } from './member-open-index-page.js';
import { showAlertMsg } from './member-show-alert-msg.js';
import { runManagerNavAction } from '../manager/manager-nav-registry.js';
import { postChargeCc } from '../shared/charge-cc.js';
import { setPaymentPayButtonDisabled } from '../shared/payment-pay-button.js';
import { watchRecaptchaChallengeClosed } from '../shared/invisible-recaptcha-challenge.js';
import { bindCardSwipeButton } from '../shared/card-swipe-button.js';

const FORM_SELECTOR = '.manual-card-form';
const INITIALIZED_ATTR = 'manualCardFormInitialized';
const formsSubmitting = new WeakSet();
const CARD_NUMBER_INPUT = FORM_SELECTOR + ' [name="card"]';
const RECAPTCHA_IDLE = 'idle';
const RECAPTCHA_EXECUTING = 'executing';
const RECAPTCHA_SUBMITTED = 'submitted';
const RECAPTCHA_CANCELLED = 'cancelled';
const recaptchaSessions = new WeakMap();
let recaptchaFormEl = null;

function namedInput($form, name) {
    return $form.find('[name="' + name + '"]');
}

function namedInputs($form, names) {
    return $form.find(names.map((name) => '[name="' + name + '"]').join(', '));
}

function setPayButtonsDisabled($form, disabled) {
    if (!disabled && document.documentElement.getAttribute('data-kiosk-payment-completed') === '1') {
        return;
    }
    $form.find('button[type="submit"]').prop('disabled', disabled);
    setPaymentPayButtonDisabled(disabled);
}

function paymentAlreadyCompleted() {
    return document.documentElement.getAttribute('data-kiosk-payment-completed') === '1';
}

function recaptchaSession(formEl) {
    let session = recaptchaSessions.get(formEl);
    if (!session) {
        session = { status: RECAPTCHA_IDLE, stopWatch: null };
        recaptchaSessions.set(formEl, session);
    }
    return session;
}

function stopRecaptchaWatch(formEl) {
    const session = recaptchaSessions.get(formEl);
    if (!session || typeof session.stopWatch !== 'function') {
        return;
    }
    session.stopWatch();
    session.stopWatch = null;
}

function unlockPayButtonsIfPaymentStillActive($form, session) {
    if (!session || session.status === RECAPTCHA_SUBMITTED || paymentAlreadyCompleted()) {
        return;
    }
    setPayButtonsDisabled($form, false);
}

function cancelRecaptcha(formEl) {
    if (!formEl) {
        return;
    }
    const session = recaptchaSession(formEl);
    stopRecaptchaWatch(formEl);
    if (session.status === RECAPTCHA_EXECUTING) {
        session.status = RECAPTCHA_CANCELLED;
        unlockPayButtonsIfPaymentStillActive($(formEl), session);
    }
}

function onRecaptchaToken(token) {
    const formEl = recaptchaFormEl;
    if (!formEl) {
        return;
    }
    const session = recaptchaSession(formEl);
    session.status = RECAPTCHA_SUBMITTED;
    stopRecaptchaWatch(formEl);
    recaptchaFormEl = null;
    const $form = $(formEl);
    const config = readConfig($form);
    if (!config) {
        return;
    }
    submitForm(config, $form, token);
}

function onRecaptchaCancelled() {
    const formEl = recaptchaFormEl;
    recaptchaFormEl = null;
    if (formEl) {
        endFormSubmit(formEl);
    }
    cancelRecaptcha(formEl);
}

function bindRecaptchaWindowCallbacks() {
    window._submitForm = onRecaptchaToken;
    window._recaptchaPayCancelled = onRecaptchaCancelled;
}

function recaptchaReady() {
    const grecaptcha = window.grecaptcha;
    return Boolean(
        grecaptcha
        && typeof grecaptcha.reset === 'function'
        && typeof grecaptcha.execute === 'function'
    );
}

function recaptchaWidgetId($form) {
    const el = $form.find('.g-recaptcha').get(0);
    if (!el) {
        return undefined;
    }
    const index = Array.prototype.indexOf.call(document.querySelectorAll('.g-recaptcha'), el);
    return index >= 0 ? index : undefined;
}

function resetAndExecuteRecaptcha($form) {
    const widgetId = recaptchaWidgetId($form);
    if (widgetId === undefined) {
        window.grecaptcha.reset();
        return window.grecaptcha.execute();
    }
    window.grecaptcha.reset(widgetId);
    return window.grecaptcha.execute(widgetId);
}

function startInvisibleRecaptcha($form) {
    const formEl = $form.get(0);
    if (recaptchaFormEl && recaptchaFormEl !== formEl) {
        cancelRecaptcha(recaptchaFormEl);
    }
    const session = recaptchaSession(formEl);
    stopRecaptchaWatch(formEl);
    session.status = RECAPTCHA_EXECUTING;
    recaptchaFormEl = formEl;
    bindRecaptchaWindowCallbacks();
    session.stopWatch = watchRecaptchaChallengeClosed(function () {
        if (session.status !== RECAPTCHA_EXECUTING) {
            return;
        }
        session.status = RECAPTCHA_CANCELLED;
        recaptchaFormEl = null;
        unlockPayButtonsIfPaymentStillActive($form, session);
    });
    const executeResult = resetAndExecuteRecaptcha($form);
    if (executeResult && typeof executeResult.catch === 'function') {
        executeResult.catch(function () {
            if (recaptchaFormEl === formEl) {
                onRecaptchaCancelled();
            }
        });
    }
}

function readConfig($form) {
    const el = $form.find('.manual-card-form-config').get(0);
    if (!el) return null;
    try {
        return JSON.parse(el.textContent || '{}');
    } catch (err) {
        console.error('manual-card-form: invalid manual-card-form-config JSON', err);
        return null;
    }
}

function selectedCardType($form) {
    const el = $form.get(0);
    return el && el.dataset.selectedCardType ? el.dataset.selectedCardType : '';
}

function setCardTypeForForm($form, cardType) {
    const el = $form.get(0);
    if (!el) {
        return;
    }
    const prev = selectedCardType($form);
    el.dataset.selectedCardType = cardType;
    if (prev && prev !== cardType) {
        namedInputs($form, ['card', 'cvv2', 'expiry_month', 'expiry_year', 'avs_zip']).val('');
    }

    const $types = $form.find('.manual-card-form__card-type');
    if ($types.length) {
        $types.removeClass('is-selected').attr('aria-pressed', 'false');
        $types.filter(`[data-card-type="${cardType}"]`).addClass('is-selected').attr('aria-pressed', 'true');
    }

    const isCredit = cardType === 'credit';
    FieldValidation.syncCvvFieldForCardType($form.find('#cvv2'), cardType);
    $form.find('#avs_zip').prop('required', isCredit);
}

function validateForm($form) {
    return FieldValidation.validateCardPaymentFields($form, null, {
        requireCreditFields: selectedCardType($form) === 'credit',
    });
}

function nonceError(message) {
    const err = new Error(message);
    err.name = 'NonceError';
    return err;
}

async function fetchNonce(config, $form) {
    const body = {
        card: String(namedInput($form, 'card').val() || '').replace(/ /g, ''),
        cvv2: namedInput($form, 'cvv2').val(),
    };

    let response;
    try {
        response = await fetch(config.tokenUrl, {
            method: 'POST',
            headers: {
                'Authorization': 'Basic ' + btoa(config.gatewayApi + ':'),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
        });
    } catch (err) {
        console.error('manual-card-form: nonce request failed', err);
        throw nonceError('There was an error creating a nonce token, please try again.');
    }
    if (!response.ok) {
        throw nonceError('There was an error creating a nonce token, please try again.');
    }
    let data;
    try {
        data = await response.json();
    } catch (err) {
        console.error('manual-card-form: nonce response was not JSON', err);
        throw nonceError('There was an error creating a nonce token, please try again.');
    }
    if (!data?.nonce_token) {
        throw nonceError(data?.error_message || 'Unknown tokenization error');
    }
    return data.nonce_token;
}

function buildChargeContext(config, $form) {
    const data = {
        ...config.sessionData,
        user_type: config.userType,
        added_by: config.addedBy,
        checkout_intent: checkoutIntentFor($form),
        _token: config.csrfToken,
    };

    if (config.userId) {
        data.user_id = config.userId;
    } else {
        data.save_type = 'add';
    }

    const congregationId = getKioskConfig().congregationId;
    if (congregationId) {
        data.congregation_id = congregationId;
    }

    return data;
}

function buildRequestData(config, $form, nonceToken, cardType, recaptchaToken) {
    const cardNumber = String(namedInput($form, 'card').val() || '').replace(/ /g, '');
    const isDafCard = cardType === 'pledger' || cardType === 'donors_fund' || cardType === 'ojc';
    return {
        ...buildChargeContext(config, $form),
        name: namedInput($form, 'name').val(),
        expiryMonth: namedInput($form, 'expiry_month').val(),
        expiryYear: FieldValidation.fullExpiryYear(namedInput($form, 'expiry_year').val()),
        avs_zip: namedInput($form, 'avs_zip').val(),
        cvv2: namedInput($form, 'cvv2').val(),
        maskedCard: cardNumber.slice(-4),
        daf_card_type: cardType,
        daf_card: isDafCard ? cardNumber : null,
        save_card: namedInput($form, 'save_card_pop').prop('checked'),
        use_otp: namedInput($form, 'save_card_pop').prop('checked') && namedInput($form, 'use_otp').prop('checked'),
        nonce: nonceToken || '',
        process_type: isDafCard ? 'direct' : 'nonce',
        payment_type: 'new',
        'g-recaptcha-response': recaptchaToken || null,
    };
}

function buildSwipeRequestData(config, $form, swipeResult) {
    return {
        ...buildChargeContext(config, $form),
        name: swipeResult.name,
        maskedCard: swipeResult.maskedCard,
        cardType: swipeResult.cardType,
        last4: swipeResult.last4,
        expiryMonth: swipeResult.expiryMonth,
        expiryYear: swipeResult.expiryYear,
        save_card: namedInput($form, 'save_card_pop').prop('checked'),
        nonce: swipeResult.nonce,
        process_type: 'nonce',
        payment_type: 'new',
        type: 'swipe',
        is_swipe: true,
    };
}

function handleSuccessResponse($form, response) {
    const $modal = $form.closest('.modal');
    const hasError = response.error && response.error !== '';

    try {
        if ($modal.length) {
            $modal.modal('hide');
            cleanupBootstrapModalBody();
        }
    } catch (err) {
        console.error('manual-card-form: close modal after charge failed', err);
    }

    setPayButtonsDisabled($form, true);
    try {
        localStorage.clear();
    } catch (err) {
        console.error('manual-card-form: localStorage.clear failed', err);
    }

    if (document.getElementById('mgr_index_paging')) {
        applyChargeSuccess(response, {
            afterInject: function () {
                runManagerNavAction('initMgrIndexDropdowns');
                if (document.getElementById('pledges-grid')) {
                    initPledgesPageContent();
                }
                const msg = hasError
                    ? response.error
                    : 'The payment was successfully processed';
                if (hasError) {
                    showAlertMsg(msg, 'danger');
                } else {
                    showAlertMsg(msg);
                }
            },
        });
        return;
    }

    try {
        if (!htmlShowsPaymentReceivedPage(response.page_info)) {
            if (hasError && !response.payment_processed) {
                showAlertMsg(response.error, 'danger');
            } else if (!response.payment_processed && response.msg) {
                showAlertMsg('Thank you for your payment!  ' + response.msg);
            } else if (!response.payment_processed && response.email_sent === false) {
                showAlertMsg('Thank you for your payment! No receipt was sent, please contact the administrator for a receipt.');
            }
        }
    } catch (err) {
        console.error('manual-card-form: post-charge alert failed', err);
    }

    applyChargeSuccess(response);
}

function postCharge(config, $form, requestData) {
    postChargeCc(
        config.chargeUrl,
        requestData,
        (response) => {
            if (chargeWentThrough(response)) {
                if (response.success) {
                    try {
                        clearCheckoutIntent($form);
                    } catch (err) {
                        console.error('manual-card-form: clearCheckoutIntent failed', err);
                    }
                }
                handleSuccessResponse($form, response);
                return;
            }
            hideOverlay(getMemberOverlayElement());
            endFormSubmit($form.get(0));
            setPayButtonsDisabled($form, false);
            if (response.error) {
                showAlertMsg(response.error, 'danger');
            }
        },
        (e) => {
            const $modal = $form.closest('.modal');
            if ($modal.length) {
                $modal.modal('hide');
                cleanupBootstrapModalBody();
            }
            endFormSubmit($form.get(0));
            setPayButtonsDisabled($form, false);
            hideOverlay(getMemberOverlayElement());
            showAlertMsg('An error occurred: ' + (e?.message || ''), 'danger');
            openIndexPage();
        },
    );
}

function submitSwipe(config, $form, swipeResult) {
    if (paymentAlreadyCompleted()) {
        return;
    }
    if (!swipeResult.nonce) {
        showAlertMsg('The swiped card could not be read. Please try again.', 'danger');
        return;
    }
    setPayButtonsDisabled($form, true);
    showOverlay(getMemberOverlayElement());
    postCharge(config, $form, buildSwipeRequestData(config, $form, swipeResult));
}

export function bindManualCardFormSwipe(formEl, { wrap, button }) {
    const $form = $(formEl);
    const config = readConfig($form);
    if (!config) {
        return Promise.resolve(false);
    }
    return bindCardSwipeButton({
        wrap,
        button,
        gatewayJavascriptUrl: config.gatewayJavascriptUrl,
        gatewayApi: config.gatewayApi,
        onSwipe: (result) => submitSwipe(config, $form, result),
        onError: (message) => showAlertMsg(message, 'danger'),
    });
}

async function submitForm(config, $form, recaptchaToken) {
    try {
        showOverlay(getMemberOverlayElement());
        const cardType = selectedCardType($form);
        const isDafCard = cardType === 'pledger' || cardType === 'donors_fund' || cardType === 'ojc';
        let nonceToken = null;

        if (!isDafCard) {
            nonceToken = await fetchNonce(config, $form);
        }

        postCharge(config, $form, buildRequestData(config, $form, nonceToken, cardType, recaptchaToken));
    } catch (err) {
        console.error('manual-card-form: submit failed', err);
        endFormSubmit($form.get(0));
        setPayButtonsDisabled($form, false);
        hideOverlay(getMemberOverlayElement());
        if (err && err.name === 'NonceError') {
            cleanupBootstrapModalBody();
            showAlertMsg(err.message, 'danger');
            return;
        }
        showAlertMsg('There was an error processing your payment. Please try again.', 'danger');
    }
}

export function syncSaveCardOtpOption($form) {
    const $save = namedInput($form, 'save_card_pop');
    const $otp = namedInput($form, 'use_otp');
    const $otpOption = $form.find('[data-otp-option]');
    if (!$save.length || !$otp.length) return;

    const saveEnabled = !$save.prop('disabled') && $save.prop('checked');
    $otp.prop('disabled', !saveEnabled);
    if (!saveEnabled) {
        $otp.prop('checked', false);
    }
    $otpOption.toggleClass('is-disabled', !saveEnabled);
}

function resetForm($form) {
    namedInputs($form, ['name', 'card', 'expiry_month', 'expiry_year', 'cvv2', 'avs_zip']).val('');
    namedInput($form, 'save_card_pop').prop('checked', false).prop('disabled', false);
    namedInput($form, 'use_otp').prop('checked', false);
    setPayButtonsDisabled($form, false);
    $form.find('#avs_zip').prop('required', true);
    FieldValidation.syncCvvFieldForCardType($form.find('#cvv2'), 'credit');
    setCardTypeForForm($form, 'credit');
    syncSaveCardOtpOption($form);
}

function attachModalFormBehavior($form, config) {
    const $modal = $form.closest('.modal');
    if (!$modal.length) return;

    $modal.on('hidden.bs.modal', function () {
        resetForm($form);
        cleanupBootstrapModalBody();
    });

    if (config.qrCodePage) {
        $modal.on('hide.bs.modal', function () {
            $.ajax({
                url: config.clearQrSessionUrl,
                type: 'GET',
                success: () => {
                    openIndexPage();
                },
                error: () => {
                    showAlertMsg(AJAX_TIMEOUT_MESSAGE, 'danger');
                    openIndexPage();
                }
            });
        });
    }
}

function initForm(formEl) {
    const $form = $(formEl);
    if (formEl.dataset[INITIALIZED_ATTR] === '1') {
        return;
    }

    const config = readConfig($form);
    if (!config) {
        return;
    }

    if (typeof $.fn.tooltip === 'function') {
        $form.find('[data-toggle="tooltip"]').tooltip();
    }
    FieldValidation.applyCardConstraints($form);

    if (!config.isKiosk && typeof $.fn.inputmask === 'function') {
        FieldValidation.applyCardMasks($form);
    }

    $form.find('.manual-card-form__card-type').on('click', function () {
        setCardTypeForForm($form, $(this).data('cardType'));
    });

    namedInput($form, 'save_card_pop').on('change', function () {
        syncSaveCardOtpOption($form);
    });
    const markedType = $form.find('.manual-card-form__card-type.is-selected').data('cardType');
    setCardTypeForForm($form, markedType || 'credit');
    syncSaveCardOtpOption($form);
    attachModalFormBehavior($form, config);
    formEl.dataset[INITIALIZED_ATTR] = '1';
}

function endFormSubmit(formEl) {
    formsSubmitting.delete(formEl);
}

function onManualCardFormSubmit(event) {
    const formEl = event.target.closest(FORM_SELECTOR);
    if (!formEl) {
        return;
    }
    event.preventDefault();

    if (paymentAlreadyCompleted() || formsSubmitting.has(formEl)) {
        return;
    }

    const $form = $(formEl);
    const config = readConfig($form);
    if (!config) {
        throw new Error('manual-card-form: missing form config');
    }

    formsSubmitting.add(formEl);
    initForm(formEl);
    setPayButtonsDisabled($form, true);

    if (!validateForm($form)) {
        endFormSubmit(formEl);
        setPayButtonsDisabled($form, false);
        return;
    }

    if (config.requireRecaptcha && recaptchaReady()) {
        startInvisibleRecaptcha($form);
        return;
    }

    submitForm(config, $form, null);
}

function bindManualCardFormDelegates() {
    if (document.documentElement.hasAttribute('data-manual-card-form-delegates')) {
        return;
    }
    document.documentElement.setAttribute('data-manual-card-form-delegates', '1');
    document.addEventListener('submit', onManualCardFormSubmit, true);
    $(document).on('input.manualCardNumberFormat', CARD_NUMBER_INPUT, function () {
        formatCardNumberInput(this);
    });
}

bindManualCardFormDelegates();

function initAll() {
    $(FORM_SELECTOR).each(function () {
        initForm(this);
    });
}

export function openManualCardModal() {
    const $modal = $('#manualCardModal');
    if (!$modal.length) {
        return;
    }
    const $form = $modal.find('.manual-card-form');
    const config = readConfig($form);
    if (!config) {
        $modal.modal('show');
        return;
    }

    setCardTypeForForm($form, 'credit');

    if (config.frequencyName && config.frequencyName !== 'One Time') {
        $form.find('.manual-card-form__option--save').hide();
        namedInput($form, 'save_card_pop').prop('checked', true).prop('disabled', true);
    } else {
        $form.find('.manual-card-form__option--save').show();
        namedInput($form, 'save_card_pop').prop('checked', false).prop('disabled', false);
    }
    syncSaveCardOtpOption($form);

    $modal.modal('show');
}

export function closeManualCardModal() {
    const $modal = $('#manualCardModal');
    if (!$modal.length) {
        return;
    }
    $modal.modal('hide');
    const $form = $modal.find('.manual-card-form');
    resetForm($form);
    cleanupBootstrapModalBody();
}

$(document).on('click', '[data-action="close-manual-card-modal"]', function (e) {
    e.preventDefault();
    closeManualCardModal();
});

export function setCardType(cardType) {
    $(FORM_SELECTOR).each(function () {
        setCardTypeForForm($(this), cardType);
    });
}

export const initManualCardForm = initForm;
export { initAll };
